import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  CreateShareRequest,
  CreateShareResponse,
  PaginatedResponse,
  Node,
  Share,
  UploadPresignRequest,
  UploadPresignResponse,
  ShareAccessResponse,
} from '@hongweibing-netdisk/shared';

const AUTH_STORAGE_KEY = 'auth-storage';

export interface AuthStorage {
  state?: { accessToken?: string | null; refreshToken?: string | null };
}

function readAuthState(): AuthStorage['state'] {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return undefined;
    return (JSON.parse(raw) as AuthStorage).state;
  } catch {
    return undefined;
  }
}

function writeTokens(accessToken: string | null, refreshToken?: string | null) {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as AuthStorage) : {};
    parsed.state = parsed.state || {};
    parsed.state.accessToken = accessToken;
    if (refreshToken !== undefined) parsed.state.refreshToken = refreshToken;
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsed));
  } catch { /* ignore */ }
}

export function getAccessToken(): string | null {
  return readAuthState()?.accessToken || null;
}

export function getRefreshToken(): string | null {
  return readAuthState()?.refreshToken || null;
}

export function setAccessToken(token: string | null) {
  writeTokens(token);
}

export function setTokens(accessToken: string, refreshToken: string) {
  writeTokens(accessToken, refreshToken);
  window.dispatchEvent(new CustomEvent('auth:refreshed', { detail: { accessToken, refreshToken } }));
}

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
const SITE_BASE = API_BASE.replace(/\/api\/v1$/, '');

export interface UploadInitResponse {
  instant?: boolean;
  nodeId: string;
  key?: string;
  size: number;
  mode?: 'single' | 'multipart' | 'proxy';
  uploadUrl?: string;
  method?: 'PUT';
  uploadId?: string;
  partSize?: number;
  partCount?: number;
}

// 共享的刷新 Promise，避免并发请求触发多次刷新
let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.accessToken && data?.refreshToken) {
      setTokens(data.accessToken, data.refreshToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function ensureFreshToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

async function request<T>(path: string, options: RequestInit = {}, retried = false): Promise<T> {
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData) && options.body != null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // 令牌过期：自动刷新并重试一次
  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    const ok = await ensureFreshToken();
    if (ok) {
      return request<T>(path, options, true);
    }
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
  }

  if (!res.ok) {
    let message = `请求失败 (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch { /* not json */ }
    if (res.status === 401 && retried) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    const err: any = new Error(message);
    err.status = res.status;
    err.code = res.status;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  auth: {
    login: (data: LoginRequest) => request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
    register: (data: RegisterRequest) => request<AuthResponse>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
    refresh: (refreshToken: string) => request<{ accessToken: string; refreshToken: string }>('/auth/refresh', { method: 'POST', body: JSON.stringify({ refreshToken }) }),
    logout: () => request('/auth/logout', { method: 'POST' }),
  },
  users: {
    me: () => request<any>('/users/me'),
    usage: () => request<{ used: number; quota: number }>('/users/me/usage'),
    updateMe: (data: { name?: string }) => request<any>('/users/me', { method: 'PATCH', body: JSON.stringify(data) }),
    changePassword: (data: { oldPassword: string; newPassword: string }) => request('/users/me/password', { method: 'PATCH', body: JSON.stringify(data) }),
  },
  nodes: {
    list: (params: { parentId?: string | null; page?: number; size?: number; q?: string; order?: string }) => {
      const qs = new URLSearchParams();
      if (params.parentId) qs.set('parent_id', params.parentId);
      if (params.page) qs.set('page', String(params.page));
      if (params.size) qs.set('size', String(params.size));
      if (params.q) qs.set('q', params.q);
      if (params.order) qs.set('order', params.order);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<PaginatedResponse<Node>>(`/nodes${suffix}`);
    },
    createFolder: (data: { parentId: string | null; name: string }) => request<Node>('/nodes', { method: 'POST', body: JSON.stringify(data) }),
    rename: (id: string, name: string) => request<Node>(`/nodes/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
    move: (id: string, parentId: string | null) => request<Node>(`/nodes/${id}`, { method: 'PATCH', body: JSON.stringify({ parentId }) }),
    toTrash: (id: string) => request(`/nodes/${id}`, { method: 'DELETE' }),
    permanentDelete: (id: string) => request(`/nodes/${id}?permanent=true`, { method: 'DELETE' }),
    downloadUrl: (id: string) => `${API_BASE}/nodes/${id}/download`,
  },
  trash: {
    list: (page = 1) => request<PaginatedResponse<any>>(`/trash?page=${page}`),
    restore: (id: string) => request(`/trash/${id}/restore`, { method: 'POST' }),
    purge: (id: string) => request(`/trash/${id}`, { method: 'DELETE' }),
    empty: () => request(`/trash`, { method: 'DELETE' }),
  },
  upload: {
    presign: (data: UploadPresignRequest) => request<UploadPresignResponse>('/upload/presign', { method: 'POST', body: JSON.stringify(data) }),
    init: (data: { filename: string; mimeType: string; size: number; checksum?: string; parentId: string | null }) =>
      request<UploadInitResponse>('/upload/init', { method: 'POST', body: JSON.stringify(data) }),
    signPart: (data: { nodeId: string; uploadId: string; partNumber: number }) =>
      request<{ partNumber: number; url: string }>('/upload/sign-part', { method: 'POST', body: JSON.stringify(data) }),
    complete: (data: { nodeId: string; uploadId?: string; parts?: Array<{ partNumber: number; etag: string }> }) =>
      request<{ ok: boolean; nodeId: string }>('/upload/complete', { method: 'POST', body: JSON.stringify(data) }),
    abort: (data: { nodeId: string; uploadId?: string }) =>
      request<{ ok: boolean }>('/upload/abort', { method: 'POST', body: JSON.stringify(data) }),
  },
  shares: {
    create: (data: CreateShareRequest) => request<CreateShareResponse>('/shares', { method: 'POST', body: JSON.stringify(data) }),
    list: () => request<Array<Share & { nodeName: string; protected: boolean; shareUrl: string }>>('/shares'),
    revoke: (code: string) => request(`/shares/${code}`, { method: 'DELETE' }),
  },
  search: (q: string, type?: 'file' | 'folder') => {
    const qs = new URLSearchParams({ q });
    if (type) qs.set('type', type);
    return request<PaginatedResponse<Node>>(`/search?${qs.toString()}`);
  },
  admin: {
    stats: () => request<{ users: number; files: number; storageUsed: number; shares: number }>('/admin/stats'),
    users: (page = 1) => request<PaginatedResponse<any>>(`/admin/users?page=${page}`),
    setQuota: (userId: string, quotaBytes: number) => request(`/admin/users/quota`, { method: 'PATCH', body: JSON.stringify({ userId, quotaBytes }) }),
    auditLogs: (page = 1) => request<PaginatedResponse<any>>(`/admin/audit-logs?page=${page}`),
  },
  publicShare: {
    get: (code: string, password?: string) => {
      const qs = password ? `?password=${encodeURIComponent(password)}` : '';
      return publicGet<ShareAccessResponse>(`/s/${code}${qs}`);
    },
    downloadUrl: (code: string, password?: string, inline = false) =>
      `${SITE_BASE}/s/${code}/download?${new URLSearchParams(password ? { password, inline: inline ? '1' : '' } : { inline: inline ? '1' : '' }).toString()}`,
  },
};

async function publicGet<T>(path: string): Promise<T> {
  const res = await fetch(`${SITE_BASE}${path}`, { headers: { Accept: 'application/json' } });
  if (!res.ok) {
    let message = `请求失败 (${res.status})`;
    try { const b = await res.json(); if (b?.error) message = b.error; } catch {}
    const err: any = new Error(message);
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}
